import React, { useState } from 'react'
import { ArrowPathIcon } from '@heroicons/react/24/outline'
import {
  useGetBusinessKnowledgeEventsQuery,
  useGetModelPerformanceFiltersQuery,
} from '../store/api/modelPerformanceApi'

const ALL = 'all'

const BusinessKnowledgeEventsTab: React.FC = () => {
  const [projectId, setProjectId] = useState(ALL)
  const { data: filterResp } = useGetModelPerformanceFiltersQuery()
  const projects = filterResp?.data?.projects || []

  const { data, isLoading, isError, refetch, isFetching } = useGetBusinessKnowledgeEventsQuery({
    projectId: projectId === ALL ? undefined : projectId,
    limit: 80,
    includeRejected: true,
  })

  const payload = data?.data
  const events = payload?.events || []

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3 justify-between">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">Business Knowledge events</h2>
          <p className="text-sm text-slate-600 max-w-2xl">
            Reconciler audit trail for the Business Planner — accepted and rejected claim ops.
            Use this to verify authority hygiene (no inference→owner-confirmed promotions).
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-sm text-slate-600">
            Project
            <select
              className="ml-2 rounded border border-slate-300 px-2 py-1 text-sm"
              value={projectId}
              onChange={(e) => setProjectId(e.target.value)}
            >
              <option value={ALL}>All</option>
              {projects.map((p) => (
                <option key={p.id} value={String(p.id)}>
                  {p.name || `#${p.id}`}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            onClick={() => refetch()}
            className="inline-flex items-center gap-1 rounded border border-slate-300 px-2 py-1 text-sm text-slate-700 hover:bg-slate-50"
          >
            <ArrowPathIcon className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {!payload?.available && !isLoading && (
        <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          Business Knowledge tables are not available on this database yet (migration pending).
        </div>
      )}

      {isError && (
        <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
          Failed to load Business Knowledge events.
        </div>
      )}

      {isLoading ? (
        <div className="text-sm text-slate-500">Loading…</div>
      ) : (
        <div className="overflow-x-auto rounded border border-slate-200">
          <table className="min-w-full text-left text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-3 py-2">When</th>
                <th className="px-3 py-2">Project</th>
                <th className="px-3 py-2">Op</th>
                <th className="px-3 py-2">Result</th>
                <th className="px-3 py-2">Actor</th>
                <th className="px-3 py-2">Summary</th>
              </tr>
            </thead>
            <tbody>
              {events.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-3 py-6 text-center text-slate-500">
                    No claim events yet.
                  </td>
                </tr>
              )}
              {events.map((e) => {
                const accepted = e.accepted === true || e.accepted === 1
                return (
                  <tr key={e.uuid} className="border-t border-slate-100 align-top">
                    <td className="px-3 py-2 whitespace-nowrap text-slate-600">
                      {e.createdAt ? new Date(e.createdAt).toLocaleString() : '—'}
                    </td>
                    <td className="px-3 py-2 text-slate-700">
                      {e.projectName || (e.projectId != null ? `#${e.projectId}` : '—')}
                    </td>
                    <td className="px-3 py-2 font-mono text-xs">{e.op}</td>
                    <td className="px-3 py-2">
                      {accepted ? (
                        <span className="text-emerald-700">accepted</span>
                      ) : (
                        <span className="text-amber-800">
                          rejected{e.rejectReason ? `: ${e.rejectReason}` : ''}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-slate-600">
                      {e.actorSource || '—'}
                      {e.actorUserId != null ? ` (#${e.actorUserId})` : ''}
                    </td>
                    <td className="px-3 py-2 text-slate-800 max-w-md">
                      {e.summary || '—'}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

export default BusinessKnowledgeEventsTab
